/**
 * @fileoverview Price preset vocabulary + preset → wire mapping for the
 * `/discover` price filter (WEB-656 / DPL-06).
 *
 * DPL-06 replaced the continuous `PriceRangeSlider` with three mutually
 * exclusive presets — **Free / $0–25 / $25+** — because a slider over sparse,
 * mostly-unknown price data is precision theatre: `DiscoverEvent` carries
 * `priceMin` / `priceMax` / `isFree` / `priceKnown`, and most events have no
 * published price at all.
 *
 * ## The trap this file exists to avoid
 *
 * A near-identical bug shipped and stayed silent for seven weeks on the
 * `time=` filter: `TimeOfDayPicker` emitted UI slugs the backend's Zod schema
 * didn't recognise, Zod silently dropped the unknown enum value, and the
 * filter *looked* like it worked end-to-end (URL updated, one request fired,
 * results returned) while the server ignored it entirely.
 *
 * The Discover backend does **not** accept a price-preset vocabulary —
 * `discoverQuerySchema` (`@loci/validation`) only understands numeric
 * `priceMin` / `priceMax` in display dollars. If a UI preset slug (`'free'`,
 * `'0-25'`, `'25-plus'`) were forwarded raw as the legacy `price` wire field,
 * it would be silently dropped the exact same way. This module is the single
 * place that translates the UI vocabulary into the numeric wire shape —
 * `buildDiscoverQueryArgs` (`lib/discover/queryArgs.ts`) and every other
 * `DiscoverEventsQueryArgs` builder MUST route through
 * {@link mapPricePresetToWire} rather than forwarding
 * `params.price` as a raw string.
 *
 * `MobileFilterDrawer` no longer has its own builder — it consumes the shared
 * `useDiscoverEventArgs` hook, because a locally reconstructed args object
 * diverged from `buildDiscoverQueryArgs`'s cache key and fired a duplicate
 * `/events/discover` request.
 *
 * ## Events with no listed price — WEB-1230 (ruling R6)
 *
 * The backend filter matches each preset against the event's published
 * `priceMin` / `priceMax` range (a declared-free event is priced `0`). Events
 * with no listed price cannot satisfy a price claim, so a preset leaves them
 * out — but never silently: the same `/events/discover` response carries
 * `facets.priceUnlisted`, and `DiscoverPricePresets` shows it as a
 * "+N not listed" toggle that sends `includeUnpriced=true` to put them back.
 * With a preset active, the toggle-off total plus that count is the toggle-on
 * total.
 *
 * Nothing here guesses a price: an unknown price is never promoted into
 * "Free" or "$25+". The **cleared** state (no preset selected —
 * `params.price === undefined`) emits no `priceMin` / `priceMax` wire args at
 * all, so the default cold load filters nothing. See
 * `buildDiscoverQueryArgs`'s cold-load byte-parity test in `queryArgs.test.ts`.
 *
 * @since WEB-656 (DPL-06 — Filter rail: Free / $0–25 / $25+ presets)
 *
 * ## WEB-730 addendum — disabling presets that are confirmed empty
 *
 * A live production pull (2026-08-11) measured price-known coverage at
 * ~1.1% (1 of 89 Charlotte events), worse than the ~3% this file originally
 * estimated. With unlisted prices left out, **every** preset could return
 * zero results. Three clickable presets that
 * can only ever return an empty state is the same dishonest affordance this
 * milestone exists to remove — just inverted. {@link derivePricePresetFacetCounts}
 * derives a live per-preset count from the discover response's inline
 * `facets.priceHistogram` (already on every `/events/discover` payload — no
 * second request), so `DiscoverPricePresets` can disable a preset that is
 * confirmed to return nothing, mirroring the WEB-659 / DPL-09 pattern
 * (`DiscoverFacetChecklistView`'s `disableZeroCount`). This re-enables
 * automatically once PIP-161's price extraction lifts coverage — nothing
 * here hardcodes today's near-zero state.
 */

/** Canonical, URL-safe preset ids. Mutually exclusive — at most one is active. */
export const PRICE_PRESETS = ['free', '0-25', '25-plus'] as const;

export type PricePreset = (typeof PRICE_PRESETS)[number];

/** Human labels for the active-filter chip row and the picker itself. */
export const PRICE_PRESET_LABELS: Record<PricePreset, string> = {
  free: 'Free',
  '0-25': 'Under $25',
  '25-plus': '$25+',
};

const PRICE_PRESET_SET: ReadonlySet<string> = new Set<PricePreset>(PRICE_PRESETS);

/** Narrow an arbitrary string to a {@link PricePreset} when it matches exactly. */
export function isPricePreset(value: string): value is PricePreset {
  return PRICE_PRESET_SET.has(value);
}

/**
 * Wire-format price bounds a preset maps to, in the display-dollar units
 * `DiscoverEventsQueryArgs.priceMin` / `priceMax` already accept.
 * `priceMax` is omitted for the open-ended `'25-plus'` preset.
 */
export interface PricePresetWireBounds {
  readonly priceMin: number;
  readonly priceMax?: number;
}

const PRICE_PRESET_WIRE: Record<PricePreset, PricePresetWireBounds> = {
  free: { priceMin: 0, priceMax: 0 },
  '0-25': { priceMin: 0, priceMax: 25 },
  '25-plus': { priceMin: 25 },
};

/**
 * Translate a canonical {@link PricePreset} into explicit `priceMin` /
 * `priceMax` wire bounds. Returns `undefined` for the cleared state
 * (`undefined` preset) so callers never emit a price filter when the user
 * hasn't selected one — this is the invariant that keeps the surface honest
 * (see the fileoverview doc above).
 */
export function mapPricePresetToWire(
  preset: PricePreset | undefined,
): PricePresetWireBounds | undefined {
  if (preset === undefined) return undefined;
  return PRICE_PRESET_WIRE[preset];
}

/**
 * Fixed backend histogram bucket labels (`DISCOVER_PRICE_BUCKETS` in
 * `services/backend-api/src/services/discoverFacets.ts`), in display-dollar
 * units, inclusive on both ends except the open-ended top bucket:
 *
 *   `'0-0'` (exactly $0) · `'1-10'` · `'11-25'` · `'26-50'` · `'51+'`
 *
 * `DiscoverPriceBucket.bucket` is typed as a plain `string` (the shared type
 * doesn't pin the literal set, mirroring how category/neighborhood facet
 * slugs are also plain strings) — this local union is the web app's own
 * pinned copy, used only to look bucket counts up by label.
 */
type PriceHistogramBucketLabel = '0-0' | '1-10' | '11-25' | '26-50' | '51+';

/**
 * Which histogram buckets feed each preset's live facet count (WEB-730).
 *
 * The histogram buckets each event by the LOW end of its listed range (its
 * "from" price; free = `0-0`), and the backend matches a preset when the
 * event's range overlaps it (WEB-1230). So:
 *
 *   - `free` (`[0, 0]`) is exactly the `0-0` bucket.
 *   - `0-25` (`low <= 25`) is exactly `0-0` + `1-10` + `11-25`.
 *   - `25-plus` (`high >= 25`) can include an event whose low end is anywhere
 *     (a $10–$40 show), and the histogram cannot see the high end. Its count
 *     is therefore every listed event: an upper bound. That is the safe
 *     direction for a control whose purpose is "never look clickable when it
 *     can only return empty" — `25-plus` is disabled only when no event has a
 *     listed price at all, never when it might still match something.
 */
const PRICE_PRESET_BUCKET_LABELS: Record<PricePreset, readonly PriceHistogramBucketLabel[]> = {
  free: ['0-0'],
  '0-25': ['0-0', '1-10', '11-25'],
  '25-plus': ['0-0', '1-10', '11-25', '26-50', '51+'],
};

/**
 * Bucket shape consumed by {@link derivePricePresetFacetCounts}. Structurally
 * matches `DiscoverPriceBucket` from `@loci/types` (`{ bucket, count }`) —
 * declared locally so this pure module has no runtime dependency on that
 * package; callers pass `facets.priceHistogram` directly.
 */
export interface PriceHistogramBucket {
  readonly bucket: string;
  readonly count: number;
}

/**
 * Derive each preset's live event count from the discover response's inline
 * `facets.priceHistogram` (WEB-730). Never issues a request of its own —
 * callers source `priceHistogram` from the SAME shared `/events/discover`
 * subscription every other rail molecule already reads (the cold-load
 * single-fetch invariant), never a dedicated facets probe.
 *
 * A bucket label absent from the input (defensive — the backend always
 * returns all five) counts as `0`.
 */
export function derivePricePresetFacetCounts(
  priceHistogram: readonly PriceHistogramBucket[],
): Record<PricePreset, number> {
  const countByBucket = new Map<string, number>(
    priceHistogram.map((entry) => [entry.bucket, entry.count]),
  );
  const counts = {} as Record<PricePreset, number>;
  for (const preset of PRICE_PRESETS) {
    counts[preset] = PRICE_PRESET_BUCKET_LABELS[preset].reduce(
      (sum, label) => sum + (countByBucket.get(label) ?? 0),
      0,
    );
  }
  return counts;
}

/** Matches the legacy dual-thumb slider's `price=min-max` / `price=min+` URL shape. */
const LEGACY_RANGE_RE = /^(\d+)-(\d+|\+)$/;

/**
 * Normalise a raw `price` URL value into a canonical {@link PricePreset},
 * migrating the retired `PriceRangeSlider`'s `min-max` / `min+` grammar
 * (including its `0-0` "Free only" shortcut) so an old bookmarked or shared
 * `/discover` link never throws and never carries a value the new picker
 * can't represent.
 *
 * Migration is **exact-match only**. A legacy range that merely *overlaps* a
 * preset is ambiguous, and resolving it would silently rewrite what the viewer
 * asked for (`0-10` → `0-25` adds $11–25; `10-+` → `25-plus` drops $10–24;
 * `25-50` → `25-plus` adds $51+). Clearing widens results instead of inventing
 * a bound they never chose, and is what `5-10` has always done — so all
 * ambiguous ranges now behave the same way.
 *
 * Mapping rules, applied in order:
 *   1. Already-canonical values (`free`, `0-25`, `25-plus`) pass through.
 *   2. `0-0` maps to `free` (the old "Free only" shortcut).
 *   3. `0-25` maps to `0-25`; `25-+` maps to `25-plus` — the only two legacy
 *      ranges whose bounds match a preset exactly.
 *   4. Every other legacy range clears the filter (`undefined`) — whether it
 *      overlaps a preset (`0-10`, `10-+`, `25-50`) or straddles bucket
 *      boundaries with no candidate at all (`5-10`, `10-50`).
 *   5. Malformed input also clears. Never throws.
 */
export function normalizePricePresetParam(raw: string | null | undefined): PricePreset | undefined {
  if (!raw) return undefined;
  if (isPricePreset(raw)) return raw;

  const match = LEGACY_RANGE_RE.exec(raw);
  if (!match) return undefined;

  const min = Number(match[1]);
  const maxToken = match[2];
  const max = maxToken === '+' ? null : Number(maxToken);

  if (!Number.isFinite(min) || min < 0) return undefined;
  if (max !== null && (!Number.isFinite(max) || max < min)) return undefined;

  // Migrate ONLY ranges that land on a preset without changing what the viewer
  // asked for. A legacy range that merely *overlaps* a preset is ambiguous, and
  // resolving it silently rewrites a saved filter's meaning:
  //   `0-10` → `0-25`     would ADD $11–25 the viewer had excluded
  //   `10-+` → `25-plus`  would DROP $10–24 the viewer had included
  // Neither is more honest than clearing, and `5-10` already cleared — so
  // resolving those two lossily made three equally-ambiguous inputs behave
  // three different ways. Ambiguous ranges now uniformly clear, which widens
  // results rather than inventing a bound the viewer never chose. WEB-656 only
  // requires legacy values to normalize sensibly or clear, and never throw.
  if (min === 0 && max === 0) return 'free';
  if (min === 0 && max === 25) return '0-25';
  if (min === 25 && max === null) return '25-plus';
  return undefined;
}
